import { ChangeDetectionStrategy, Component, OnInit, ViewChild, ElementRef, AfterViewChecked, signal, inject, effect } from '@angular/core';
import { ChatMessage, AppUser, AiStateContext } from "./ai-chat.model";
import { MenuItem, MessageService } from "primeng/api";
import { AiChatService } from "./ai-chat.service";
import { SessionStorageService } from "../services/session-storage.service";
import { ToastModule } from "primeng/toast";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { ProgressBarModule } from "primeng/progressbar";
import { InputTextModule } from "primeng/inputtext";
import { TagModule } from "primeng/tag";
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { AiChatHtmlFormatterService } from './ai-chat-html-formatter.service';
import { ActivatedRoute } from '@angular/router';


@Component({
    selector: 'app-ai-chat',
    templateUrl: './ai-chat.component.html',
    styleUrls: ['./ai-chat.component.css'],
    imports: [
        CommonModule,
        TagModule,
        FormsModule,
        ReactiveFormsModule,
        ToastModule,
        BreadcrumbModule,
        ButtonModule,
        ProgressBarModule,
        InputTextModule,
        PageShellComponent,
        PageHeroComponent,
        PageDataPanelComponent
    ],


    providers: [
        AiChatService,
        SessionStorageService,
        MessageService
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatComponent implements OnInit, AfterViewChecked {
    private aiChatService = inject(AiChatService);
    private messageService = inject(MessageService);
    private sessionStorageService = inject(SessionStorageService);
    private htmlFormatter = inject(AiChatHtmlFormatterService);
    private activatedRoute = inject(ActivatedRoute);

    @ViewChild('chatMessages') private chatMessages: ElementRef;

    public messages = signal<ChatMessage[]>([]);
    public newMessage = signal<string>('');
    public isLoading = signal<boolean>(false);

    private customerId: number;
    private companyId: number;
    private stateContext: AiStateContext = {};

    public breadcrumbItems = signal<MenuItem[]>([]);
    public homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    public currentUser: AppUser = { userRole: 'CUSTOMER' };
    public botUser: AppUser = { userRole: 'AI_AGENT', fullName: 'AI Assistant' };

    constructor() {
        // Effect to scroll to bottom when messages change
        effect(() => {
            if (this.messages().length > 0) {
                setTimeout(() => this.scrollToBottom(), 0);
            }
        });
    }

    ngOnInit(): void {
        const customer = this.sessionStorageService.getCustomer();
        const company = this.sessionStorageService.getCompany();

        if (customer) {
            this.customerId = customer.id;
            this.currentUser = {
                ...this.currentUser,
                fullName: (customer as any).fullName || (customer as any).name,
                email: (customer as any).email,
                mobileNumber: (customer as any).mobileNumber,
                firebaseId: (customer as any).firebaseId
            };
        }
        if (company) this.companyId = company.id;

        this.messages.set([]);

        const query = this.activatedRoute.snapshot.queryParamMap;
        this.stateContext = {
            routeUrl: query.get('routeUrl') || undefined,
            pageTitle: query.get('pageTitle') || undefined,
            module: query.get('module') || undefined,
            selectedRecords: query.get('manualId')
                ? { manualId: query.get('manualId') }
                : undefined
        };

        this.breadcrumbItems.set([{ label: 'AI Chat', routerLink: '/ai-chat' }]);
    }

    sendMessage(): void {
        const messageText = this.newMessage().trim();
        if (messageText !== '') {
            const chatMessage: ChatMessage = {
                sender: this.currentUser,
                textContent: messageText,
                timestamp: new Date(),
                customerId: this.customerId,
                companyId: this.companyId,
                stateContext: this.stateContext
            };

            this.messages.update(prev => [...prev, chatMessage]);
            this.newMessage.set('');
            this.isLoading.set(true);

            this.aiChatService.sendMessage(chatMessage).subscribe({
                next: (response) => {
                    this.messages.update(prev => [...prev, response]);
                    this.isLoading.set(false);
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to send message.'
                    });
                    console.error(error);
                    this.isLoading.set(false);
                }
            });
        }
    }

    sendActionButton(payload: string): void {
        const chatMessage: ChatMessage = {
            sender: this.currentUser,
            textContent: payload,
            timestamp: new Date(),
            customerId: this.customerId,
            companyId: this.companyId,
            stateContext: this.stateContext
        };

        this.messages.update(prev => [...prev, chatMessage]);
        this.isLoading.set(true);

        this.aiChatService.sendMessage(chatMessage).subscribe({
            next: (response) => {
                this.messages.update(prev => [...prev, response]);
                this.isLoading.set(false);
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to process action.'
                });
                this.isLoading.set(false);
            }
        });
    }

    ngAfterViewChecked(): void {
        this.scrollToBottom();
    }

    convertToHtml(rawText: string, hasStructuredSources = false) {
        return this.htmlFormatter.convertToHtml(rawText, hasStructuredSources);
    }

    scrollToBottom(): void {
        try {
            if (this.chatMessages) {
                this.chatMessages.nativeElement.scrollTop = this.chatMessages.nativeElement.scrollHeight;
            }
        } catch (err) {
            // Error handling for scroll
        }
    }
}
